import React, { useState } from 'react';

const ManualTenantWizard = ({ onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // School Contact Information
    schoolName: '',
    subdomain: '',
    primaryAdminEmail: '',
    allowedEmailDomains: [''],
    schoolPhone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    contactPersonName: '',
    contactPersonTitle: '',
    contactPersonPhone: '',
    contactPersonEmail: '',
    
    // Organizational Structure
    schoolType: '',
    studentEnrollment: '',
    facultyStaffCount: '',
    calendarType: '',
    departments: [''],
    
    // Initial Configuration
    timeZone: '',
    defaultLanguage: 'en',
    academicYearStart: '',
    academicYearEnd: '',
    gradingPeriods: '',
    
    // Branding
    primaryColor: '#1e40af',
    secondaryColor: '#3b82f6',
    schoolMascot: '',
    
    // Integrations
    googleWorkspace: false,
    microsoft365: false,
    sisSystem: '',
    otherIntegrations: ''
  });

  const steps = [
    { id: 1, title: 'School Information' },
    { id: 2, title: 'Organization' },
    { id: 3, title: 'Configuration' },
    { id: 4, title: 'Branding' },
    { id: 5, title: 'Integrations' },
    { id: 6, title: 'Review & Create' }
  ];

  const schoolTypes = [
    'Early Childhood',
    'Elementary School',
    'Middle School', 
    'High School',
    'K-5',
    'K-8',
    'K-12 School',
    'Charter School',
    'Private School',
    'Magnet School',
    'Other'
  ];

  // EXPANDED TIMEZONE LIST WITH INTERNATIONAL OPTIONS
  const timeZones = [
    { value: 'America/New_York', label: 'Eastern Time (US)' },
    { value: 'America/Chicago', label: 'Central Time (US)' },
    { value: 'America/Denver', label: 'Mountain Time (US)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
    { value: 'America/Anchorage', label: 'Alaska Time (US)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (US)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    { value: 'Europe/Rome', label: 'Rome (CET)' },
    { value: 'Europe/Madrid', label: 'Madrid (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)' },
    { value: 'Asia/Shanghai', label: 'Beijing/Shanghai (CST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
    { value: 'America/Toronto', label: 'Toronto (EST)' },
    { value: 'America/Vancouver', label: 'Vancouver (PST)' }
  ];

  const gradingPeriodOptions = [
    'Quarters (4 periods)',
    'Trimesters (3 periods)',
    'Semesters (2 periods)',
    'Custom'
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const generateSubdomain = (schoolName) => {
    return schoolName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 30);
  };

  const handleSchoolNameChange = (value) => {
    updateFormData('schoolName', value);
    if (!formData.subdomain || formData.subdomain === generateSubdomain(formData.schoolName)) {
      updateFormData('subdomain', generateSubdomain(value));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.schoolName && formData.subdomain && formData.primaryAdminEmail && 
               formData.allowedEmailDomains[0] && formData.contactPersonName;
      case 2:
        return formData.schoolType && formData.studentEnrollment && formData.facultyStaffCount;
      case 3:
        return formData.timeZone && formData.academicYearStart && formData.academicYearEnd;
      case 4:
        return true; // Branding is optional
      case 5:
        return true; // Integrations are optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error creating tenant:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontSize: '14px',
    fontFamily: 'sans-serif'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '14px',
    fontFamily: 'sans-serif'
  };

  const buttonStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    fontFamily: 'sans-serif'
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>
                  Full School Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.schoolName}
                  onChange={(e) => handleSchoolNameChange(e.target.value)}
                  placeholder="e.g., Northwood High School"
                  style={inputStyle}
                  required
                />
              </div>
              
              <div>
                <label style={labelStyle}>
                  Subdomain Slug <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ display: 'flex' }}>
                  <input
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) => updateFormData('subdomain', e.target.value)}
                    placeholder="e.g., northwood"
                    style={{ ...inputStyle, borderRadius: '4px 0 0 4px', borderRight: 'none' }}
                    required
                  />
                  <span style={{ 
                    padding: '10px', 
                    backgroundColor: '#f5f5f5', 
                    border: '1px solid #ccc', 
                    borderLeft: 'none',
                    borderRadius: '0 4px 4px 0', 
                    fontSize: '14px', 
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    .reliefroster.com
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                  The live URL will be: <strong>{formData.subdomain || '...'}.reliefroster.com</strong>
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>
                  Admin Email Address <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.primaryAdminEmail}
                  onChange={(e) => updateFormData('primaryAdminEmail', e.target.value)}
                  placeholder="admin@yourschool.org"
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>School Phone</label>
                <input
                  type="tel"
                  value={formData.schoolPhone}
                  onChange={(e) => updateFormData('schoolPhone', e.target.value)}
                  placeholder="(555) 123-4567"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Allowed Email Domains <span style={{ color: 'red' }}>*</span>
              </label>
              {formData.allowedEmailDomains.map((domain, index) => (
                <div key={index} style={{ display: 'flex', marginBottom: '10px', gap: '10px' }}>
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => updateArrayItem('allowedEmailDomains', index, e.target.value)}
                    placeholder="yourschool.org"
                    style={{ ...inputStyle, flex: 1 }}
                    required={index === 0}
                  />
                  {formData.allowedEmailDomains.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('allowedEmailDomains', index)}
                      style={{ ...buttonStyle, backgroundColor: '#dc3545', color: 'white' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('allowedEmailDomains')}
                style={{ ...buttonStyle, backgroundColor: '#007bff', color: 'white', fontSize: '12px' }}
              >
                + Add another domain
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>School Address</label>
              <input
                type="text"
                value={formData.streetAddress}
                onChange={(e) => updateFormData('streetAddress', e.target.value)}
                placeholder="Street Address"
                style={{ ...inputStyle, marginBottom: '10px' }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  placeholder="City"
                  style={inputStyle}
                />
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateFormData('state', e.target.value)}
                  placeholder="State"
                  style={inputStyle}
                />
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => updateFormData('zipCode', e.target.value)}
                  placeholder="ZIP Code"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ borderTop: '1px solid #ddd', paddingTop: '20px' }}>
              <h4 style={{ marginBottom: '15px', color: '#333' }}>Primary Contact Person</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>
                    Contact Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.contactPersonName}
                    onChange={(e) => updateFormData('contactPersonName', e.target.value)}
                    placeholder="John Doe"
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Title/Position</label>
                  <input
                    type="text"
                    value={formData.contactPersonTitle}
                    onChange={(e) => updateFormData('contactPersonTitle', e.target.value)}
                    placeholder="Principal, IT Director, etc."
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>
                  School Type <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={formData.schoolType}
                  onChange={(e) => updateFormData('schoolType', e.target.value)}
                  style={{ ...inputStyle, backgroundColor: 'white' }}
                  required
                >
                  <option value="">Select school type</option>
                  {schoolTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Calendar Type</label>
                <select
                  value={formData.calendarType}
                  onChange={(e) => updateFormData('calendarType', e.target.value)}
                  style={{ ...inputStyle, backgroundColor: 'white' }}
                >
                  <option value="">Select calendar type</option>
                  <option value="traditional">Traditional (Aug-June)</option>
                  <option value="year-round">Year-Round</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>
                  Student Enrollment <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  value={formData.studentEnrollment}
                  onChange={(e) => updateFormData('studentEnrollment', e.target.value)}
                  placeholder="500"
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Faculty/Staff Count <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  value={formData.facultyStaffCount}
                  onChange={(e) => updateFormData('facultyStaffCount', e.target.value)}
                  placeholder="50"
                  style={inputStyle}
                  required
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Departments/Groups</label>
              {formData.departments.map((dept, index) => (
                <div key={index} style={{ display: 'flex', marginBottom: '10px', gap: '10px' }}>
                  <input
                    type="text"
                    value={dept}
                    onChange={(e) => updateArrayItem('departments', index, e.target.value)}
                    placeholder="Math, Science, Administration, etc."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  {formData.departments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('departments', index)}
                      style={{ ...buttonStyle, backgroundColor: '#dc3545', color: 'white' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('departments')}
                style={{ ...buttonStyle, backgroundColor: '#007bff', color: 'white', fontSize: '12px' }}
              >
                + Add department
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>
                  Time Zone <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={formData.timeZone}
                  onChange={(e) => updateFormData('timeZone', e.target.value)}
                  style={{ ...inputStyle, backgroundColor: 'white' }}
                  required
                >
                  <option value="">Select time zone</option>
                  {timeZones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Default Language</label>
                <select
                  value={formData.defaultLanguage}
                  onChange={(e) => updateFormData('defaultLanguage', e.target.value)}
                  style={{ ...inputStyle, backgroundColor: 'white' }}
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>
                  Academic Year Start <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.academicYearStart}
                  onChange={(e) => updateFormData('academicYearStart', e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Academic Year End <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.academicYearEnd}
                  onChange={(e) => updateFormData('academicYearEnd', e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Grading Periods</label>
              <select
                value={formData.gradingPeriods}
                onChange={(e) => updateFormData('gradingPeriods', e.target.value)}
                style={{ ...inputStyle, backgroundColor: 'white' }}
              >
                <option value="">Select grading period structure</option>
                {gradingPeriodOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>Primary Brand Color</label>
                <div style={{ display: 'flex' }}>
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => updateFormData('primaryColor', e.target.value)}
                    style={{ width: '50px', height: '40px', border: '1px solid #ccc', borderRadius: '4px 0 0 4px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) => updateFormData('primaryColor', e.target.value)}
                    style={{ ...inputStyle, borderRadius: '0 4px 4px 0', borderLeft: 'none', flex: 1 }}
                    placeholder="#1e40af"
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Secondary Brand Color</label>
                <div style={{ display: 'flex' }}>
                  <input
                    type="color"
                    value={formData.secondaryColor}
                    onChange={(e) => updateFormData('secondaryColor', e.target.value)}
                    style={{ width: '50px', height: '40px', border: '1px solid #ccc', borderRadius: '4px 0 0 4px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={formData.secondaryColor}
                    onChange={(e) => updateFormData('secondaryColor', e.target.value)}
                    style={{ ...inputStyle, borderRadius: '0 4px 4px 0', borderLeft: 'none', flex: 1 }}
                    placeholder="#3b82f6"
                  />
                </div>
              </div>
            </div>

            <div>
              <label style={labelStyle}>School Mascot/Team Name</label>
              <input
                type="text"
                value={formData.schoolMascot}
                onChange={(e) => updateFormData('schoolMascot', e.target.value)}
                placeholder="Eagles, Lions, Warriors, etc."
                style={inputStyle}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <input
                  type="checkbox"
                  id="googleWorkspace"
                  checked={formData.googleWorkspace}
                  onChange={(e) => updateFormData('googleWorkspace', e.target.checked)}
                  style={{ marginRight: '10px' }}
                />
                <label htmlFor="googleWorkspace" style={{ fontSize: '14px', color: '#333' }}>
                  Google Workspace Integration
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <input
                  type="checkbox"
                  id="microsoft365"
                  checked={formData.microsoft365}
                  onChange={(e) => updateFormData('microsoft365', e.target.checked)}
                  style={{ marginRight: '10px' }}
                />
                <label htmlFor="microsoft365" style={{ fontSize: '14px', color: '#333' }}>
                  Microsoft 365 Integration
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Student Information System (SIS)</label>
              <select
                value={formData.sisSystem}
                onChange={(e) => updateFormData('sisSystem', e.target.value)}
                style={{ ...inputStyle, backgroundColor: 'white' }}
              >
                <option value="">Select SIS (if applicable)</option>
                  <option value="powerschool">PowerSchool</option>
                  <option value="infinite-campus">Infinite Campus</option>
                  <option value="skyward">Skyward</option>
                  <option value="studentplus">Student Plus</option>
                  <option value="Veracross">Veracross</option>
                  <option value="FACTS">FACTS</option>
                  <option value="Admin PLUS">Admin PLUS</option>
                  <option value="Big SIS">Big SIS</option>
                  <option value="iSAMS">iSAMS</option>
                  <option value="other">Other</option>
                  <option value="none">No SIS</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Other Integrations or Requirements</label>
              <textarea
                value={formData.otherIntegrations}
                onChange={(e) => updateFormData('otherIntegrations', e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                placeholder="Describe any other systems needed or special requirements..."
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div>
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #2196f3', 
              borderRadius: '4px', 
              padding: '15px', 
              marginBottom: '20px' 
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1976d2', fontSize: '16px' }}>Ready to Create Tenant</h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#1976d2' }}>
                Review the information below and click "Create New School Tenant" to set up {formData.schoolName}.
              </p>
            </div>

            <div style={{ display: 'grid', gap: '15px' }}>
              <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>School Information</h4>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <p style={{ margin: '5px 0' }}><strong>School:</strong> {formData.schoolName}</p>
                  <p style={{ margin: '5px 0' }}><strong>URL:</strong> {formData.subdomain}.reliefroster.com</p>
                  <p style={{ margin: '5px 0' }}><strong>Admin:</strong> {formData.primaryAdminEmail}</p>
                  <p style={{ margin: '5px 0' }}><strong>Contact:</strong> {formData.contactPersonName}</p>
                </div>
              </div>

              <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Organization</h4>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <p style={{ margin: '5px 0' }}><strong>Type:</strong> {formData.schoolType}</p>
                  <p style={{ margin: '5px 0' }}><strong>Students:</strong> {formData.studentEnrollment}</p>
                  <p style={{ margin: '5px 0' }}><strong>Staff:</strong> {formData.facultyStaffCount}</p>
                  <p style={{ margin: '5px 0' }}><strong>Time Zone:</strong> {timeZones.find(tz => tz.value === formData.timeZone)?.label}</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        fontFamily: 'sans-serif'
      }}>
        {/* Header */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '20px', 
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
            Manual School Setup
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            ×
          </button>
        </div>

        {/* Progress Steps */}
        <div style={{ 
          padding: '15px 20px', 
          borderBottom: '1px solid #dee2e6',
          backgroundColor: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {steps.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              
              return (
                <div key={step.id} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    backgroundColor: isCompleted ? '#28a745' : isActive ? '#007bff' : '#e9ecef',
                    color: isCompleted || isActive ? 'white' : '#6c757d'
                  }}>
                    {isCompleted ? '✓' : step.id}
                  </div>
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: isActive ? '#007bff' : isCompleted ? '#28a745' : '#6c757d',
                    display: window.innerWidth > 768 ? 'block' : 'none'
                  }}>
                    {step.title}
                  </span>
                  {step.id < steps.length && (
                    <div style={{
                      height: '2px',
                      backgroundColor: isCompleted ? '#28a745' : '#e9ecef',
                      flex: 1,
                      marginLeft: '10px',
                      display: window.innerWidth > 768 ? 'block' : 'none'
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div style={{ 
          padding: '20px', 
          maxHeight: '60vh', 
          overflowY: 'auto' 
        }}>
          {renderStep()}
        </div>

        {/* Footer with Navigation */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '20px', 
          borderTop: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            style={{
              ...buttonStyle,
              backgroundColor: currentStep === 1 ? '#e9ecef' : '#6c757d',
              color: currentStep === 1 ? '#6c757d' : 'white',
              cursor: currentStep === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            ← Previous
          </button>

          <div style={{ 
            fontSize: '14px', 
            color: '#6c757d',
            fontWeight: '500'
          }}>
            Step {currentStep} of {steps.length}
          </div>

          {currentStep < 6 ? (
            <button
              onClick={nextStep}
              disabled={!validateStep(currentStep)}
              style={{
                ...buttonStyle,
                backgroundColor: validateStep(currentStep) ? '#007bff' : '#e9ecef',
                color: validateStep(currentStep) ? 'white' : '#6c757d',
                cursor: validateStep(currentStep) ? 'pointer' : 'not-allowed'
              }}
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !validateStep(currentStep)}
              style={{
                ...buttonStyle,
                backgroundColor: (isSubmitting || !validateStep(currentStep)) ? '#e9ecef' : '#28a745',
                color: (isSubmitting || !validateStep(currentStep)) ? '#6c757d' : 'white',
                cursor: (isSubmitting || !validateStep(currentStep)) ? 'not-allowed' : 'pointer',
                padding: '12px 24px'
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create New School Tenant'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualTenantWizard;